// ============================================================================
// ADR-0024 A-1 — report questions panel (audit-scope questions)
// ============================================================================
// The Auditor's Report and CARO are driven by the auditor's judgements —
// opinion type, going concern, CARO / IFC applicability, audit trail, each
// CARO clause. Those questions carry `scope: "audit"` in the component and are
// answered HERE, inside the report editor, not in the Corporate Facts pane.
//
// Each answer is saved at once to the engagement's facts (`audit_answers`,
// merged server-side), not with the report's Save — the Auditor's Report and
// CARO read the same answer (e.g. "Is CARO applicable?"), and the variant
// shown in the editor switches immediately.
//
// ADR-0024 N-2 — in a note the panel also carries the note's policy choices
// (`scope: "note"` → `note_answers`, carried to next year and badged "from
// previous year" until confirmed) and the sections' INPUTS: figures and text
// for this year (`note_inputs`), committed on blur. An input declared
// `scope: "standing"` (deed date, nature of business…) is kept in
// `note_standing` and carried to the next year.

import React, { useEffect, useState } from 'react';
import type { CorporateFactsData } from 'api/types/fsa/corporateFacts.types';
import type { ApplicabilitySuggestion } from 'api/types/fsa/reportData.types';
import { answerOf, inputScope, inputValueOf, type ApplicabilityQuestion, type VisibleInput } from './reportSections';

/** Where an answer is stored: audit_answers, note_answers, note_inputs or note_standing. */
export type AnswerScope = 'audit' | 'note' | 'inputs' | 'standing';

interface Props {
  questions: ApplicabilityQuestion[];
  facts: CorporateFactsData | undefined;
  suggestions?: Record<string, ApplicabilitySuggestion>;
  readOnly?: boolean;
  onSetAnswer?: (code: string, value: boolean | string | number | null, scope: AnswerScope) => Promise<void>;
  /** Panel heading — "Report questions" (default) or, in a note, "Note questions". */
  title?: string;
  /** ADR-0024 N-2 — inputs of the sections whose answer shows them. */
  inputs?: VisibleInput[];
  /** Inside the document side panel: always open, full height, rows wrap to the panel's width. */
  embedded?: boolean;
}

const ReportQuestionsPanel: React.FC<Props> = ({ questions, facts, suggestions, readOnly, onSetAnswer, title = 'Report questions', inputs = [], embedded = false }) => {
  const carried = new Set(facts?.note_answers_carried ?? []);
  const emptyInputs = inputs.filter(i => {
    const v = inputValueOf(i, facts);
    return v === undefined || v === null || (typeof v === 'string' && !v.trim());
  }).length;
  const unanswered = questions.filter(q => answerOf(q, facts) === undefined).length
    + questions.filter(q => q.scope === 'note' && carried.has(q.code)).length
    + emptyInputs;
  const [open, setOpen] = useState(unanswered > 0);
  const [saving, setSaving] = useState<string | null>(null);

  if (questions.length === 0 && inputs.length === 0) return null;

  const set = async (code: string, value: boolean | string | number | null, scope: AnswerScope) => {
    if (!onSetAnswer || readOnly) return;
    setSaving(code);
    try { await onSetAnswer(code, value, scope); } finally { setSaving(null); }
  };

  const row: React.CSSProperties = embedded ? { ...rowStyle, flexWrap: 'wrap', padding: '10px 0' } : rowStyle;
  return (
    <div style={embedded ? embeddedPanelStyle : panelStyle}>
      {embedded ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 0 6px' }}>
          <span style={unanswered > 0 ? warnPillStyle : okPillStyle}>
            {unanswered > 0 ? `${unanswered} to answer` : 'All answered'}
          </span>
          <span style={{ color: 'var(--text-secondary)' }}>Answers choose the wording; saved straight away.</span>
        </div>
      ) : (
      <button type="button" style={toggleStyle} onClick={() => setOpen(o => !o)}>
        <span style={unanswered > 0 ? warnPillStyle : okPillStyle}>
          {unanswered > 0 ? `${unanswered} to answer` : 'All answered'}
        </span>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          · the answers choose the wording; saved straight away and shared with the related documents
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{open ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      )}
      {(open || embedded) && (
        <div style={embedded ? undefined : listStyle}>
          {questions.map(q => {
            const value = answerOf(q, facts);
            const suggestion = suggestions?.[q.code];
            const choices: Array<{ value: boolean | string; label: string }> = q.options
              ? q.options.map(o => ({ value: o.value, label: o.label }))
              : [{ value: true, label: 'Yes' }, { value: false, label: 'No' }];
            const defaultLabel = q.defaultAnswer == null
              ? null
              : choices.find(c => c.value === q.defaultAnswer)?.label ?? null;
            return (
              <div key={q.code} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-primary)' }}>
                    {q.prompt}
                    {q.scope === 'note' && carried.has(q.code) && (
                      <span style={carriedPillStyle} title="Answer carried from the previous year — click it (or another answer) to confirm for this year">
                        from previous year
                      </span>
                    )}
                  </div>
                  <div style={subtleStyle}>
                    {suggestion && (
                      <div style={{ color: '#1e40af' }} title={suggestion.note ?? undefined}>
                        Suggested from the saved figures: <strong>{suggestion.answer ? 'Yes' : 'No'}</strong> — {suggestion.reason}
                        {suggestion.note ? ' (approximation — hover for details)' : ''}
                      </div>
                    )}
                    Drives: {q.sectionTitles.join(', ')}
                    {value === undefined && defaultLabel && ` · default: ${defaultLabel}`}
                  </div>
                </div>
                <div style={segmentedStyle} aria-busy={saving === q.code}>
                  {choices.map(c => (
                    <button
                      key={String(c.value)}
                      type="button"
                      disabled={readOnly || saving != null}
                      onClick={() => set(q.code, c.value, q.scope === 'note' ? 'note' : 'audit')}
                      style={segStyle(value === c.value)}
                    >
                      {c.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={readOnly || saving != null || value === undefined}
                    onClick={() => set(q.code, null, q.scope === 'note' ? 'note' : 'audit')}
                    style={segStyle(value === undefined, true)}
                    title="Clear — the section's default applies"
                  >
                    —
                  </button>
                </div>
              </div>
            );
          })}
          {inputs.map(input => (
            <InputRow
              key={input.code}
              input={input}
              value={inputValueOf(input, facts)}
              disabled={readOnly || saving != null}
              onCommit={v => set(input.code, v, inputScope(input))}
              embedded={embedded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** ADR-0024 N-2 — one section input: amount or text, committed on blur. */
const InputRow: React.FC<{
  input: VisibleInput;
  value: number | string | undefined;
  disabled: boolean;
  onCommit: (value: number | string | null) => void;
  embedded?: boolean;
}> = ({ input, value, disabled, onCommit, embedded }) => {
  const saved = value === undefined || value === null ? '' : String(value);
  const [text, setText] = useState(saved);
  useEffect(() => { setText(saved); }, [saved]);
  const commit = () => {
    const t = text.trim();
    if (t === saved.trim()) return;
    if (!t) { onCommit(null); return; }
    if (input.kind === 'amount') {
      const n = Number(t.replace(/,/g, ''));
      if (!Number.isFinite(n)) { setText(saved); return; }
      onCommit(n);
    } else {
      onCommit(t);
    }
  };
  return (
    <div style={{ ...rowStyle, alignItems: input.kind === 'text' ? 'flex-start' : 'center', ...(embedded ? { flexWrap: 'wrap', padding: '10px 0' } : {}) }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--text-primary)' }}>{input.label}</div>
        <div style={subtleStyle}>For: {input.sectionTitle}{input.hint ? ` · ${input.hint}` : ''}</div>
      </div>
      {input.kind === 'amount' ? (
        <input
          type="text"
          inputMode="decimal"
          className="form-input"
          style={{ width: 160, textAlign: 'right', fontSize: '0.78rem', padding: '3px 6px' }}
          value={text}
          disabled={disabled}
          placeholder="Amount"
          onChange={e => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
      ) : (
        <textarea
          className="form-textarea"
          style={{ width: embedded ? '100%' : 360, fontSize: '0.78rem', padding: '3px 6px' }}
          rows={3}
          maxLength={4000}
          value={text}
          disabled={disabled}
          placeholder={input.hint ?? 'Text printed in the note'}
          onChange={e => setText(e.target.value)}
          onBlur={commit}
        />
      )}
    </div>
  );
};

const embeddedPanelStyle: React.CSSProperties = { fontSize: '0.8rem' };
const panelStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)', fontSize: '0.78rem', flexShrink: 0,
};
const toggleStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 14px', border: 'none',
  background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.78rem', textAlign: 'left',
};
const listStyle: React.CSSProperties = { maxHeight: 260, overflowY: 'auto', padding: '0 14px 8px' };
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderTop: '1px solid var(--border-color)',
};
const subtleStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--text-secondary)' };
const segmentedStyle: React.CSSProperties = {
  display: 'inline-flex', border: '1px solid var(--form-border-color)', borderRadius: 6, overflow: 'hidden', flexShrink: 0,
};
const segStyle = (active: boolean, muted = false): React.CSSProperties => ({
  padding: '3px 10px', border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  background: active ? (muted ? '#64748b' : 'var(--primary-color, #2563eb)') : 'transparent',
  color: active ? 'white' : 'var(--text-secondary)',
});
const carriedPillStyle: React.CSSProperties = {
  marginLeft: 8, padding: '0 6px', borderRadius: 999, background: '#ede9fe', color: '#5b21b6', fontSize: '0.64rem', fontWeight: 700,
};
const pillBase: React.CSSProperties = { padding: '1px 8px', borderRadius: 999, fontWeight: 700, fontSize: '0.68rem' };
const warnPillStyle: React.CSSProperties = { ...pillBase, background: '#fef3c7', color: '#92400e' };
const okPillStyle: React.CSSProperties = { ...pillBase, background: '#dcfce7', color: '#166534' };

export default ReportQuestionsPanel;
